import React, { useState, useMemo, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { ToolShell, Field, Select, fmt } from "./_shared";

/**
 *  PURIFICATION PLANT SIZING — v2
 *  --------------------------------------------------------------
 *  Full-fat workflow:
 *
 *    1. DEMAND  — same profile + full-time / part-time / occupancy
 *                 logic as Water Storage Sizing.  Sets permeate L/day.
 *
 *    2. SYSTEM  — operating window + recovery (freely editable).
 *                 Returns permeate L/h, feed L/h, reject L/h.
 *
 *    3. PRE-TREATMENT WASTE — user-defined list of flushed stages
 *                 (sand filter back-wash, carbon rinse, antiscalant
 *                 dosing pot flush, CIP cycles…).  Each stage:
 *                   · flush duration   (hh:mm:ss)
 *                   · flush flow-rate  (L/min  |  m³/h)
 *                   · flush frequency  (hh:mm:ss between flushes)
 *                 → litres wasted per day per stage, then totalled.
 *
 *    4. RAW WATER RESERVE — total raw water draw / day
 *                 = feed_to_RO  +  pre-treatment waste
 *                 × user-set reserve days = raw tank volume.
 * --------------------------------------------------------------
 */

/* ────────────────────────────────────────────────────────────── */
/*  Demand profiles — exact copy from StorageSizingCalculator     */
/* ────────────────────────────────────────────────────────────── */
const PROFILES = [
  { value: "luxury",     label: "Luxury 5★ lodge / villa",            lpd: 650 },
  { value: "boutique",   label: "Boutique 4★ hotel / lodge",          lpd: 400 },
  { value: "midtier",    label: "Mid-tier 3★ hospitality",            lpd: 300 },
  { value: "eco",        label: "Eco lodge / tented camp",            lpd: 200 },
  { value: "residential",label: "Residential home",                   lpd: 250 },
  { value: "estate",     label: "Estate / sectional title",           lpd: 220 },
  { value: "staff",      label: "Staff village (live-in)",            lpd: 100 },
  { value: "agri",       label: "Agricultural site / temporary camp", lpd: 50  },
  { value: "office",     label: "Office (no live-in)",                lpd: 25  },
  { value: "school",     label: "School (day pupils)",                lpd: 25  },
  { value: "restaurant", label: "Restaurant guest (per cover)",       lpd: 25  },
  { value: "conference", label: "Conference delegate (day)",          lpd: 30  },
  { value: "hospital",   label: "Hospital bed",                       lpd: 600 },
  { value: "custom",     label: "Custom — enter L/p/day below",       lpd: null },
];

const TECH_PRESETS_RETIRED = (
  // Retained as documentation only — the dropdown that seeded "recovery"
  // from a technology preset was removed Feb 2026 per editor request
  // (one number, the user-entered recovery, drives every result).
  [
    { value: "ro",       label: "Reverse osmosis · ~50 % recovery",       recovery: 50  },
    { value: "ro-high",  label: "Reverse osmosis · high-recovery (75 %)", recovery: 75  },
    { value: "nf",       label: "Nanofiltration · ~70 % recovery",        recovery: 70  },
    { value: "uf",       label: "Ultrafiltration · ~90 % recovery",       recovery: 90  },
    { value: "mf-uv",    label: "Sediment / cartridge / UV · 100 %",      recovery: 100 },
  ]
);
// eslint-disable-next-line no-unused-vars
const _ = TECH_PRESETS_RETIRED;

const DEMAND_MODES = [
  { value: "people", label: "Derive from people × L/p/day (full + part time)" },
  { value: "direct", label: "I already know the daily volume" },
];

// Risk profiles match the Water Storage tool 1:1 — the same engineering
// framework runs across both calculators so a planner can specify raw
// AND clean water storage with one decision.
const RISK_PROFILES = [
  { value: "1", label: "1 day — metro site with reliable backup" },
  { value: "2", label: "2 days — peri-urban / reticulated municipal" },
  { value: "3", label: "3 days — semi-remote / single source" },
  { value: "5", label: "5 days — remote / off-grid" },
  { value: "7", label: "7 days — critical site, no alternative supply" },
];

const FLOW_UNITS = [
  { value: "lpm",  label: "L/min" },
  { value: "m3h",  label: "m³/h" },
];

const SECONDS_PER_DAY = 86400;

/* ────────────────────────────────────────────────────────────── */
/*  Helper: HH:MM:SS time picker                                  */
/* ────────────────────────────────────────────────────────────── */
function hmsToSeconds({ h, m, s }) {
  const H = Math.max(0, parseInt(h, 10) || 0);
  const M = Math.max(0, parseInt(m, 10) || 0);
  const S = Math.max(0, parseInt(s, 10) || 0);
  return H * 3600 + M * 60 + S;
}

function HMSInput({ h, m, s, onChange, testid }) {
  const set = (k, v) => onChange({ h, m, s, [k]: v });
  return (
    <div className="flex items-stretch" data-testid={testid}>
      <HMSCell value={h} onChange={(v) => set("h", v)} ariaLabel="hours" testid={`${testid}-h`} />
      <span style={colon}>:</span>
      <HMSCell value={m} onChange={(v) => set("m", v)} ariaLabel="minutes" max={59} testid={`${testid}-m`} />
      <span style={colon}>:</span>
      <HMSCell value={s} onChange={(v) => set("s", v)} ariaLabel="seconds" max={59} testid={`${testid}-s`} />
    </div>
  );
}
const colon = {
  display: "inline-flex", alignItems: "center", padding: "0 4px",
  color: "rgba(12,42,64,0.55)", fontFamily: "ui-monospace, monospace",
};
function HMSCell({ value, onChange, ariaLabel, max = 23, testid }) {
  return (
    <input
      type="number"
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => {
        let v = parseInt(e.target.value, 10);
        if (Number.isNaN(v)) v = 0;
        v = Math.max(0, v);
        if (max !== undefined && v > max) v = max;
        onChange(v);
      }}
      data-testid={testid}
      className="min-w-0 px-2 py-2.5 bg-white border outline-none focus:border-[#1FB6C1] text-center font-mono"
      style={{
        borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
        fontSize: "0.95rem", fontWeight: 500, width: 64,
      }}
    />
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Helper: number-with-suffix                                    */
/* ────────────────────────────────────────────────────────────── */
function SuffixNum({ value, onChange, suffix, step = "any", min, testid }) {
  // Buffered text-mode input — fixes two UX bugs:
  //   (a) A field with value 2 wouldn't clear (controlled number input
  //       snapped empty → 0 → repaint as "0" before the user finished).
  //       We buffer the raw string locally so the field can sit empty.
  //   (b) "15 becomes 015":  parent state of 0 used to render as "0",
  //       then typing "15" produced "015". We treat 0 as the empty /
  //       placeholder state and never render a leading zero.
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input
        type="text"
        inputMode="decimal"
        value={buf}
        placeholder="0"
        data-testid={testid}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                 fontSize: "0.95rem", fontWeight: 500 }}
      />
      <span
        className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)",
                 color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}
      >
        {suffix}
      </span>
    </div>
  );
}

function Stat({ label, value, unit, alt, highlight, testid }) {
  return (
    <div
      className="px-4 py-3"
      style={{
        background: highlight ? "rgba(31,182,193,0.10)" : "#FFFFFF",
        border: `1px solid ${highlight ? "rgba(31,182,193,0.40)" : "rgba(12,42,64,0.10)"}`,
      }}
      data-testid={testid}
    >
      <div className="font-mono uppercase"
           style={{ fontSize: "0.58rem", letterSpacing: "0.2em", color: "rgba(12,42,64,0.55)" }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5 mt-1">
        <span style={{ fontSize: "1.35rem", fontWeight: 700, color: "#0C2A40" }}>{value}</span>
        <span className="font-mono" style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.6)" }}>{unit}</span>
      </div>
      {alt && (
        <div className="mt-1 font-light"
             style={{ fontSize: "0.7rem", color: "rgba(12,42,64,0.55)" }}>{alt}</div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Pre-treatment stage — flow_rate in L/min in base form          */
/* ────────────────────────────────────────────────────────────── */
function defaultStage(name = "") {
  return {
    id: `pre-${Math.random().toString(36).slice(2, 9)}`,
    name,
    duration:  { h: 0, m: 5,  s: 0 },     // 5 min back-wash
    frequency: { h: 24, m: 0, s: 0 },     // once a day
    flowValue: 60,                         // 60 L/min
    flowUnit:  "lpm",
  };
}

function flowLpm(stage) {
  if (stage.flowUnit === "m3h") return (stage.flowValue || 0) * 1000 / 60;
  return stage.flowValue || 0;
}

function stageWasteLitresPerDay(stage) {
  const durSec   = hmsToSeconds(stage.duration);
  const freqSec  = hmsToSeconds(stage.frequency);
  if (durSec <= 0 || freqSec <= 0) return 0;
  const lpm      = flowLpm(stage);
  const litres   = (lpm / 60) * durSec;           // L per single flush
  const perDay   = SECONDS_PER_DAY / freqSec;     // flushes / day
  return litres * perDay;
}

/* ────────────────────────────────────────────────────────────── */
/*  Main component                                                */
/* ────────────────────────────────────────────────────────────── */
export default function PurificationPlantSizing() {
  /* ─── 1. DEMAND ───────────────────────────────────────────── */
  const [demandMode,     setDemandMode]     = useState("people");

  // Direct mode
  const [dailyL,         setDailyL]         = useState(20000);

  // People mode — full copy of StorageSizingCalculator demand block
  const [profile,        setProfile]        = useState("luxury");
  const [fullTimeLpd,    setFullTimeLpd]    = useState(650);
  const [partTimeLpd,    setPartTimeLpd]    = useState(50);
  const [fullTimePersons,setFullTimePersons]= useState(40);
  const [partTimePersons,setPartTimePersons]= useState(20);
  const [occupancyPct,   setOccupancyPct]   = useState(70);

  useEffect(() => {
    const p = PROFILES.find((x) => x.value === profile);
    if (p && p.lpd != null) setFullTimeLpd(p.lpd);
  }, [profile]);

  const occ           = occupancyPct / 100;
  const fullTimeDaily = fullTimePersons * fullTimeLpd * occ;
  const partTimeDaily = partTimePersons * partTimeLpd * occ;
  const peopleDaily_L = fullTimeDaily + partTimeDaily;

  const effectiveDailyL = demandMode === "people" ? peopleDaily_L : dailyL;

  /* ─── 2. SYSTEM ───────────────────────────────────────────── */
  const [hoursPerDay,   setHoursPerDay]   = useState(16);
  const [recoveryPct,   setRecoveryPct]   = useState(50);

  const recovery   = Math.max(1, Math.min(100, recoveryPct || 1));
  const permeateLph = hoursPerDay > 0 ? effectiveDailyL / hoursPerDay : 0;
  const feedLph     = recovery > 0    ? permeateLph / (recovery / 100) : 0;
  const rejectLph   = Math.max(0, feedLph - permeateLph);

  const dailyFeedL    = feedLph * hoursPerDay;          // raw → RO membrane
  const dailyRejectL  = rejectLph * hoursPerDay;        // RO reject
  // permeate × hoursPerDay ≡ effectiveDailyL (production)

  /* ─── 3. PRE-TREATMENT WASTE ──────────────────────────────── */
  const [stages, setStages] = useState([
    { ...defaultStage("Sand filter back-wash") },
  ]);

  const addStage  = () => setStages((xs) => [...xs, defaultStage(`Stage ${xs.length + 1}`)]);
  const dropStage = (id) => setStages((xs) => xs.filter((s) => s.id !== id));
  const patchStage = (id, patch) =>
    setStages((xs) => xs.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const stageWastes = useMemo(
    () => stages.map((s) => ({ id: s.id, name: s.name, litres: stageWasteLitresPerDay(s) })),
    [stages]
  );
  const preWasteDailyL = useMemo(
    () => stageWastes.reduce((a, w) => a + (w.litres || 0), 0),
    [stageWastes]
  );

  /* ─── 4. RAW & CLEAN WATER RESERVE ─────────────────────────
     Storage requirement uses the same risk profile as the Water Storage
     Sizing tool — pick the days of self-sufficiency by site type, the
     tool computes BOTH the raw water tank (sized off total raw water
     draw) and the clean water tank (sized off daily permeate produced).
     One decision, two tanks, same risk model. */
  const [riskDays, setRiskDays] = useState("3");
  const reserveDays = parseInt(riskDays, 10) || 1;

  const totalRawDailyL  = dailyFeedL + preWasteDailyL;
  const rawReserveL     = totalRawDailyL * reserveDays;
  const cleanReserveL   = effectiveDailyL * reserveDays;

  /* ─── DERIVED COPY ───────────────────────────────────────── */
  const energyHint = "Typical 0.8–1.5 kWh per m³ permeate for brackish RO. Higher for seawater feeds. UF/MF systems are 0.1–0.3 kWh per m³.";

  return (
    <ToolShell
      slug="purification-plant"
      title="Purification plant sizing"
      oneLiner="Full demand → permeate → feed → pre-treatment waste → raw water reserve. The procurement-ready answer to 'what size plant, and what raw-water draw do I need to plan for?'"
      onExport={() => ({
        inputs: [
          ...(demandMode === "people" ? [
            { label: "Site profile",        value: PROFILES.find((p) => p.value === profile)?.label ?? profile },
            { label: "Full-time persons",   value: fullTimePersons, unit: "persons" },
            { label: "Full-time L/p/day",   value: fullTimeLpd,     unit: "L/p/day" },
            { label: "Part-time persons",   value: partTimePersons, unit: "persons" },
            { label: "Part-time L/p/day",   value: partTimeLpd,     unit: "L/p/day" },
            { label: "Average occupancy",   value: occupancyPct,    unit: "%" },
          ] : [
            { label: "Daily demand (direct)", value: dailyL, unit: "L/day" },
          ]),
          { label: "Daily demand (permeate target)", value: fmt(effectiveDailyL, 0), unit: "L/day" },
          { label: "Operating window", value: hoursPerDay, unit: "h/day" },
          { label: "Recovery rate", value: recovery, unit: "%" },
          { label: "Pre-treatment stages", value: stages.length, unit: "" },
          ...stages.map((s, i) => ({
            label: `Stage ${i + 1} · ${s.name || "(unnamed)"}`,
            value: `dur ${fmtHMS(s.duration)} · ${s.flowValue} ${s.flowUnit === "m3h" ? "m³/h" : "L/min"} · every ${fmtHMS(s.frequency)}`,
          })),
          { label: "Reserve days (risk appetite)",
            value: RISK_PROFILES.find((r) => r.value === riskDays)?.label ?? `${reserveDays} days` },
        ],
        results: [
          { label: "Permeate (product) rate", value: fmt(permeateLph, 0), unit: "L/h",
            alts: [{ value: fmt(permeateLph / 1000, 2), unit: "m³/h" }] },
          { label: "RO feed rate",            value: fmt(feedLph, 0),    unit: "L/h",
            alts: [{ value: fmt(feedLph / 1000, 2), unit: "m³/h" }] },
          { label: "RO reject / brine",       value: fmt(rejectLph, 0),  unit: "L/h",
            alts: [{ value: fmt(rejectLph / 1000, 2), unit: "m³/h" }] },
          { label: "Daily RO reject",         value: fmt(dailyRejectL, 0), unit: "L/day" },
          { label: "Pre-treatment waste",     value: fmt(preWasteDailyL, 0), unit: "L/day" },
          { label: "Total raw water draw",    value: fmt(totalRawDailyL, 0), unit: "L/day",
            alts: [{ value: fmt(totalRawDailyL / 1000, 2), unit: "m³/day" }] },
          { label: "Raw water tank reserve",  value: fmt(rawReserveL, 0), unit: "L",
            alts: [{ value: fmt(rawReserveL / 1000, 1), unit: "kL" }],
            note: `${reserveDays}-day reserve at total raw water draw` },
          { label: "Clean water tank reserve", value: fmt(cleanReserveL, 0), unit: "L",
            alts: [{ value: fmt(cleanReserveL / 1000, 1), unit: "kL" }],
            note: `${reserveDays}-day reserve at daily permeate produced` },
        ],
        notes: [
          "Permeate L/h = daily demand ÷ operating hours.",
          "Feed L/h = permeate ÷ (recovery / 100). Reject = feed − permeate.",
          "Pre-treatment waste = Σ (flow_rate × duration ÷ frequency) over all stages.",
          "Total raw water draw = daily feed to membrane + pre-treatment waste.",
          "Raw water tank = total raw water draw × reserve days.",
          "Clean water tank = daily permeate produced × reserve days. Same risk model as the Water Storage Sizing tool.",
          energyHint,
          "Reference only — the HidroVerse team will validate against feed-water chemistry, fouling potential, pre-treatment selection and operator skill before issuing a procurement spec.",
        ],
        // Navy "Supplier-ready plant specification" callout — mirrors the
        // in-app banner so the PDF carries the same headline takeaway.
        spec: {
          title: "Supplier-ready plant specification",
          body: `Specify a plant capable of <strong>${fmt(permeateLph, 0)} L/h (${fmt(permeateLph / 1000, 2)} m³/h)</strong> permeate, drawing <strong>${fmt(feedLph, 0)} L/h</strong> of feed at <strong>${recovery} %</strong> recovery, running <strong>${hoursPerDay} hours/day</strong>. Total raw water draw — including <strong>${fmt(preWasteDailyL, 0)} L/day</strong> of pre-treatment waste from ${stages.length} stage${stages.length === 1 ? "" : "s"} — is <strong>${fmt(totalRawDailyL, 0)} L/day (${fmt(totalRawDailyL / 1000, 2)} m³/day)</strong>. Provide <strong>${fmt(rawReserveL, 0)} L (${fmt(rawReserveL / 1000, 1)} kL)</strong> of raw water storage AND <strong>${fmt(cleanReserveL, 0)} L (${fmt(cleanReserveL / 1000, 1)} kL)</strong> of clean (post-treatment) water storage for <strong>${reserveDays} day${reserveDays === 1 ? "" : "s"}</strong> of self-sufficiency.<br/><br/>${energyHint} The HidroVerse team will validate against actual feed-water chemistry, pre-treatment requirements and pump duty before issuing a procurement spec.`,
        },
      })}
    >
      {/* INTRO */}
      <div className="mb-5 p-4"
           style={{ background: "#F3FBFB", border: "1px solid rgba(31,182,193,0.30)" }}
           data-testid="pps-intro-banner">
        <p className="font-light leading-relaxed"
           style={{ fontSize: "0.84rem", color: "#0C2A40" }}>
          Pull demand straight from people × L/p/day (the same model as the
          Water Storage tool), set your operating window and recovery,
          then add every pre-treatment flush cycle that wastes water —
          back-washes, rinses, CIPs. The tool returns the plant duty,
          the total raw water draw, and the raw water tank reserve for
          the days of self-sufficiency you specify.
        </p>
      </div>

      {/* ─── 1 · DEMAND ────────────────────────────────────────── */}
      <SectionHeader n="1" label="Daily water demand (permeate target)" />

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Input method" span={2}>
          <Select value={demandMode} onChange={setDemandMode} options={DEMAND_MODES} testid="pps-demand-mode" />
        </Field>

        {demandMode === "direct" && (
          <Field label="Daily volume" hint="Total potable / treated water needed per day." span={2}>
            <SuffixNum value={dailyL} onChange={setDailyL} suffix="L/day" step="100" min={0} testid="pps-daily" />
          </Field>
        )}

        {demandMode === "people" && (
          <>
            <Field label="Site profile" span={2}>
              <Select value={profile} onChange={setProfile} options={PROFILES} testid="pps-profile" />
            </Field>

            <Field label="Full-time persons"
                   hint="Guests, residents and live-in staff — bathe, shower, sleep and eat on site.">
              <SuffixNum value={fullTimePersons} onChange={setFullTimePersons}
                         suffix="persons" step="1" min={0} testid="pps-ft-persons" />
            </Field>
            <Field label="Full-time L/p/day"
                   hint="Pre-loaded from the profile · over-write for your site.">
              <SuffixNum value={fullTimeLpd} onChange={setFullTimeLpd}
                         suffix="L/p/day" step="10" min={0} testid="pps-ft-lpd" />
            </Field>

            <Field label="Part-time persons"
                   hint="Day staff, commuters, drop-in workers — do not bathe / shower on site.">
              <SuffixNum value={partTimePersons} onChange={setPartTimePersons}
                         suffix="persons" step="1" min={0} testid="pps-pt-persons" />
            </Field>
            <Field label="Part-time L/p/day"
                   hint="Toilet, hand-wash, drinking water only — typically 30–50 L/p/day.">
              <SuffixNum value={partTimeLpd} onChange={setPartTimeLpd}
                         suffix="L/p/day" step="5" min={0} testid="pps-pt-lpd" />
            </Field>

            <Field label="Average occupancy"
                   hint="100 % if permanent, lower for seasonal sites." span={2}>
              <SuffixNum value={occupancyPct} onChange={setOccupancyPct}
                         suffix="%" step="5" min={0} testid="pps-occ" />
            </Field>
          </>
        )}
      </div>

      <div className="mt-3 px-4 py-2"
           style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.08)" }}
           data-testid="pps-effective-demand">
        <span className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.2em",
                       color: "rgba(12,42,64,0.55)" }}>
          Permeate target (daily demand)
        </span>
        <span className="ml-3" style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0C2A40" }}>
          {fmt(effectiveDailyL, 0)} L/day
        </span>
        <span className="ml-2 font-mono" style={{ fontSize: "0.72rem", color: "rgba(12,42,64,0.55)" }}>
          = {fmt(effectiveDailyL / 1000, 2)} m³/day
        </span>
        {demandMode === "people" && (
          <span className="ml-3 font-light"
                style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.6)" }}>
            (full-time {fmt(fullTimeDaily, 0)} + part-time {fmt(partTimeDaily, 0)} L/day)
          </span>
        )}
      </div>

      {/* ─── 2 · SYSTEM ───────────────────────────────────────── */}
      <SectionHeader n="2" label="Operating window & recovery" topSpacing />

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Operating hours per day"
               hint="Off-peak schedule: 16 h.  Continuous: 24 h.  Shorter window = larger plant.">
          <SuffixNum value={hoursPerDay} onChange={setHoursPerDay}
                     suffix="h/day" step="1" min={1} testid="pps-hours" />
        </Field>
        <Field label="Recovery rate"
               hint="Permeate ÷ feed × 100. Brackish RO sits 50–75 %, nanofiltration ~70 %, ultrafiltration ~90 %, sediment / UV polishing 100 %.">
          <SuffixNum value={recoveryPct} onChange={setRecoveryPct}
                     suffix="%" step="1" min={1} testid="pps-recovery" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 mb-2">
        <Stat label="Permeate rate" value={fmt(permeateLph, 0)} unit="L/h"
              alt={`${fmt(permeateLph / 1000, 2)} m³/h · the plant's product rating`}
              highlight testid="pps-permeate" />
        <Stat label="Feed rate" value={fmt(feedLph, 0)} unit="L/h"
              alt={`${fmt(feedLph / 1000, 2)} m³/h · membrane feed pump duty`}
              testid="pps-feed" />
        <Stat label="Reject / brine rate" value={fmt(rejectLph, 0)} unit="L/h"
              alt={recovery === 100
                ? "no reject for this technology"
                : `${fmt(rejectLph / 1000, 2)} m³/h to disposal / reuse`}
              testid="pps-reject" />
        <Stat label="Daily RO feed total" value={fmt(dailyFeedL, 0)} unit="L/day"
              alt={`${fmt(dailyFeedL / 1000, 2)} m³/day to the membrane`}
              testid="pps-feed-total" />
      </div>

      {/* ─── 3 · PRE-TREATMENT WASTE ──────────────────────────── */}
      <SectionHeader n="3" label="Pre-treatment waste (flushes, back-washes, rinses)" topSpacing />

      <p className="font-light mb-3"
         style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.7)", lineHeight: 1.55 }}>
        Add a row for every flushed stage upstream of the membrane —
        sand filter back-wash, carbon rinse, antiscalant pot flush, CIP
        cycle, ultrafilter back-pulse.  Each row computes the litres
        wasted per day from <em>flush flow-rate × flush duration ÷ flush
        frequency</em>.  The total is added to the membrane feed to
        size the raw water reserve.
      </p>

      <div className="space-y-3" data-testid="pps-pre-stages">
        {stages.map((s, idx) => (
          <PreStageRow
            key={s.id}
            index={idx}
            stage={s}
            litresPerDay={stageWastes[idx]?.litres || 0}
            onPatch={(patch) => patchStage(s.id, patch)}
            onDrop={() => dropStage(s.id)}
          />
        ))}
      </div>

      <button type="button"
              onClick={addStage}
              data-testid="pps-add-stage"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 border font-mono"
              style={{
                background: "#FFFFFF", color: "#0C2A40",
                borderColor: "rgba(12,42,64,0.16)",
                fontSize: "0.7rem", letterSpacing: "0.1em",
                textTransform: "uppercase", fontWeight: 700,
              }}>
        <Plus size={14} /> Add pre-treatment stage
      </button>

      <div className="mt-4 grid md:grid-cols-3 gap-3">
        <Stat label="Pre-treatment waste"
              value={fmt(preWasteDailyL, 0)} unit="L/day"
              alt={`${fmt(preWasteDailyL / 1000, 2)} m³/day · sum of all stages`}
              testid="pps-pre-total" />
        <Stat label="RO reject (from membrane)"
              value={fmt(dailyRejectL, 0)} unit="L/day"
              alt={`${fmt(dailyRejectL / 1000, 2)} m³/day · at ${recovery} % recovery`}
              testid="pps-ro-reject" />
        <Stat label="Total water wastage"
              value={fmt(preWasteDailyL + dailyRejectL, 0)} unit="L/day"
              alt={`${fmt((preWasteDailyL + dailyRejectL) / 1000, 2)} m³/day · pre-treatment + RO reject`}
              testid="pps-total-waste" />
      </div>

      {/* ─── 4 · RAW & CLEAN WATER RESERVE ─────────────────────── */}
      <SectionHeader n="4" label="Raw & clean water reserve (risk-based)" topSpacing />

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Reserve days (risk appetite)"
               hint="Same risk model as the Water Storage tool. Both the raw tank (total raw water draw × days) and the clean tank (daily permeate × days) are sized off this one choice.">
          <Select value={riskDays} onChange={setRiskDays} options={RISK_PROFILES} testid="pps-risk-days" />
        </Field>
        <Stat label="Total raw water draw"
              value={fmt(totalRawDailyL, 0)} unit="L/day"
              alt={`feed ${fmt(dailyFeedL, 0)} + pre-treatment ${fmt(preWasteDailyL, 0)} L/day`}
              highlight testid="pps-raw-daily" />
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <Stat label="Raw water tank reserve"
              value={fmt(rawReserveL, 0)} unit="L"
              alt={`${fmt(rawReserveL / 1000, 1)} kL · ${reserveDays}-day reserve at total raw draw`}
              highlight testid="pps-raw-tank" />
        <Stat label="Clean water tank reserve"
              value={fmt(cleanReserveL, 0)} unit="L"
              alt={`${fmt(cleanReserveL / 1000, 1)} kL · ${reserveDays}-day reserve at daily permeate`}
              highlight testid="pps-clean-tank" />
      </div>

      <div className="mt-3 grid md:grid-cols-1 gap-3">
        <Stat label="Daily permeate produced"
              value={fmt(effectiveDailyL, 0)} unit="L/day"
              alt={`${fmt(effectiveDailyL / 1000, 2)} m³/day · matches the demand target — what the clean tank fills with each day`}
              testid="pps-perm-daily" />
      </div>

      {/* SUPPLIER SPEC */}
      <div className="mt-6 p-5"
           style={{ background: "#0C2A40", color: "#F5F7FA" }}
           data-testid="pps-spec-card">
        <div className="font-mono uppercase mb-3"
             style={{ fontSize: "0.6rem", letterSpacing: "0.22em",
                      color: "#1FB6C1", fontWeight: 700 }}>
          Supplier-ready plant specification
        </div>
        <p style={{ fontSize: "0.95rem", lineHeight: 1.65 }}>
          Specify a plant capable of{" "}
          <Em>{fmt(permeateLph, 0)} L/h ({fmt(permeateLph / 1000, 2)} m³/h)</Em>{" "}
          permeate, drawing{" "}
          <Em>{fmt(feedLph, 0)} L/h</Em>{" "}
          of feed at{" "}
          <Em>{recovery} %</Em>{" "}
          recovery, running{" "}
          <Em>{hoursPerDay} hours/day</Em>.
          Total raw water draw — including{" "}
          <Em>{fmt(preWasteDailyL, 0)} L/day</Em> of pre-treatment waste
          from {stages.length} stage{stages.length === 1 ? "" : "s"} —
          is{" "}
          <Em>{fmt(totalRawDailyL, 0)} L/day ({fmt(totalRawDailyL / 1000, 2)} m³/day)</Em>.
          Provide{" "}
          <Em>{fmt(rawReserveL, 0)} L ({fmt(rawReserveL / 1000, 1)} kL)</Em>{" "}
          of raw water storage AND{" "}
          <Em>{fmt(cleanReserveL, 0)} L ({fmt(cleanReserveL / 1000, 1)} kL)</Em>{" "}
          of clean (post-treatment) water storage for{" "}
          <Em>{reserveDays} day{reserveDays === 1 ? "" : "s"}</Em> of
          self-sufficiency.
        </p>
        <p className="mt-3 font-light"
           style={{ fontSize: "0.78rem", color: "rgba(245,247,250,0.7)", lineHeight: 1.55 }}>
          {energyHint} The HidroVerse team will validate against actual
          feed-water chemistry, pre-treatment requirements and pump duty
          before issuing a procurement spec.
        </p>
      </div>
    </ToolShell>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Sub-components                                                */
/* ────────────────────────────────────────────────────────────── */
function SectionHeader({ n, label, topSpacing }) {
  return (
    <div className={topSpacing ? "mt-7 mb-3" : "mb-3"}>
      <div className="font-mono uppercase"
           style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                    color: "#0C2A40", fontWeight: 700 }}>
        {n} · {label}
      </div>
    </div>
  );
}

function Em({ children }) {
  return <span style={{ fontWeight: 700, color: "#1FB6C1" }}>{children}</span>;
}

function fmtHMS({ h, m, s }) {
  const pad = (x) => String(x).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function PreStageRow({ index, stage, litresPerDay, onPatch, onDrop }) {
  return (
    <div className="p-3"
         style={{ background: "#FCFCFA", border: "1px solid rgba(12,42,64,0.10)" }}
         data-testid={`pps-stage-${index}`}>
      <div className="flex items-center justify-between mb-3 gap-3">
        <input
          type="text"
          value={stage.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="Stage name (e.g. Sand filter back-wash)"
          data-testid={`pps-stage-${index}-name`}
          className="flex-1 min-w-0 px-3 py-2 bg-white border outline-none focus:border-[#1FB6C1]"
          style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                   fontSize: "0.92rem", fontWeight: 500 }}
        />
        <button type="button"
                onClick={onDrop}
                title="Remove this stage"
                data-testid={`pps-stage-${index}-remove`}
                className="inline-flex items-center gap-1 px-3 py-2 font-mono"
                style={{
                  background: "#FFFFFF", color: "#B85C38",
                  border: "1px solid rgba(184,92,56,0.35)",
                  fontSize: "0.65rem", letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}>
          <Trash2 size={12} /> Remove
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Flush duration"
               hint="How long one flush runs.">
          <HMSInput
            h={stage.duration.h} m={stage.duration.m} s={stage.duration.s}
            onChange={(t) => onPatch({ duration: t })}
            testid={`pps-stage-${index}-duration`}
          />
        </Field>
        <Field label="Flush flow rate" hint="L/min or m³/h while the flush is running.">
          <div className="flex items-stretch gap-0">
            <div className="flex-1 min-w-0">
              <SuffixNum value={stage.flowValue} onChange={(v) => onPatch({ flowValue: v })}
                         testid={`pps-stage-${index}-flow`} />
            </div>
            <select
              value={stage.flowUnit}
              onChange={(e) => onPatch({ flowUnit: e.target.value })}
              data-testid={`pps-stage-${index}-flow-unit`}
              className="px-3 py-2.5 border border-l-0 bg-white font-mono"
              style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                       fontSize: "0.78rem" }}
            >
              {FLOW_UNITS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
        </Field>
        <Field label="Flush frequency"
               hint="Time between consecutive flushes (e.g. 24:00:00 = once a day).">
          <HMSInput
            h={stage.frequency.h} m={stage.frequency.m} s={stage.frequency.s}
            onChange={(t) => onPatch({ frequency: t })}
            testid={`pps-stage-${index}-frequency`}
          />
        </Field>
        <Field label="Wastage / day">
          <div className="flex items-stretch">
            <div className="flex-1 px-3 py-2.5 border bg-white font-mono"
                 style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40",
                          fontSize: "0.95rem", fontWeight: 700 }}
                 data-testid={`pps-stage-${index}-waste`}>
              {fmt(litresPerDay, 0)}
            </div>
            <span
              className="inline-flex items-center px-3 border border-l-0 font-mono"
              style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)",
                       color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}>
              L/day
            </span>
          </div>
        </Field>
      </div>
    </div>
  );
}
